// src/components/PerformanceFormModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import configData from '../../configure';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';
import 'react-toastify/dist/ReactToastify.css';
import './PerformanceFormModal.css';

const PerformanceFormModal = ({
  open,
  onClose,
  classId,
  className,
  eventId,
  academicYear = '2025-2026',
  existingData = null,
  onSuccess,
  orgId,
}) => {
  const { token, orgId: contextOrgId, user } = useAuth();
  const activeOrgId = orgId || contextOrgId || user?.orgId || '';
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [searchStudent, setSearchStudent] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');

  const [assessments, setAssessments] = useState([]);
  const [subjectNames, setSubjectNames] = useState({});
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  // Now stores both marksObtained and maxMarks per assessment
  const [performanceData, setPerformanceData] = useState({}); // { assessmentId: { marksObtained, maxMarks } }
  const [remarks, setRemarks] = useState(existingData?.remarks || '');
  const [saving, setSaving] = useState(false);

  // Grade calculation based on percentage of actual max marks
  const calculateGrade = (obtained, max) => {
    if (obtained === '' || obtained === null || obtained === undefined) return '-';
    const marks = parseInt(obtained) || 0;
    const total = parseInt(max) || 100;
    if (total === 0) return '-';
    const percentage = (marks / total) * 100;

    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  // Fetch students
  useEffect(() => {
    if (!open || !classId) return;

    const fetchStudents = async () => {
      setLoadingStudents(true);
      try {
        const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, {
          params: { pageSize: 500 },
          headers: { Authorization: token },
        });
        const list = res.data.items?.map(item => ({
          userId: item.user.userId,
          name: `${item.user.firstName || ''} ${item.user.middleName || ''} ${item.user.lastName || ''}`.trim(),
          rollNumber: item.studentInfo?.rollNumber || 'N/A',
        })) || [];
        setStudents(list);

        if (existingData?.userId) {
          setSelectedStudent(existingData.userId);
        }
      } catch (err) {
        console.error('Failed to load students', err);
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudents();
  }, [open, classId, token, existingData]);

  // Fetch assessments + subject names
  useEffect(() => {
    if (!open || !eventId || !classId) {
      setAssessments([]);
      setSubjectNames({});
      setPerformanceData({});
      return;
    }

    const fetchAssessmentsAndSubjects = async () => {
      setLoadingSubjects(true);
      try {
        // Fetch assessments
        const assResponse = await axios.get(
          configData.RESOURCES_ASSESSMENT_EVENT,
          {
            params: { eventId, pageSize: 100 },
            headers: { Authorization: token },
          }
        );

        const allAssessments = assResponse.data.items || [];
        const relevantAssessments = allAssessments.filter(ass =>
          ass.classIds?.includes(classId)
        );

        setAssessments(relevantAssessments);

        // Fetch subject names
        const courseIds = [...new Set(relevantAssessments.map(a => a.courseId))];
        const courseNameMap = {};

        await Promise.all(
          courseIds.map(async (courseId) => {
            try {
              const courseRes = await axios.get(
                configData.get_resources_resources_courses(courseId),
                { headers: { Authorization: token } }
              );
              courseNameMap[courseId] = courseRes.data.courseDetails?.courseName || 'Unknown Subject';
            } catch (err) {
              courseNameMap[courseId] = 'Unknown Subject';
            }
          })
        );

        setSubjectNames(courseNameMap);

        // Initialize performance data with existing values or defaults
        const initialData = {};
        relevantAssessments.forEach(ass => {
          const existing = existingData?.assessmentPerformanceList?.find(
            item => item.assessmentId === ass.assessmentId
          );

          initialData[ass.assessmentId] = {
            marksObtained: existing?.marksObtained || '',
            maxMarks: existing?.maxMarks || ass.maxMarks || '100', // use API maxMarks if available
          };
        });
        setPerformanceData(initialData);
      } catch (err) {
        console.error('Failed to load assessments', err);
        toast.error('Failed to load exam subjects.');
        setAssessments([]);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchAssessmentsAndSubjects();
  }, [open, eventId, classId, token, existingData]);

  const filteredStudents = useMemo(() => {
    return students.filter(s =>
      s.name.toLowerCase().includes(searchStudent.toLowerCase()) ||
      s.rollNumber.includes(searchStudent)
    );
  }, [students, searchStudent]);

  const handleMarksChange = (assessmentId, field, value) => {
    setPerformanceData(prev => ({
      ...prev,
      [assessmentId]: {
        ...prev[assessmentId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      toast.warning('Please select a student');
      return;
    }

    const performanceList = Object.entries(performanceData)
      .filter(([_, data]) => {
        const marks = parseInt(data.marksObtained);
        return !isNaN(marks) && marks >= 0;
      })
      .map(([assessmentId, data]) => {
        const assessment = assessments.find(a => a.assessmentId === assessmentId);
        const max = parseInt(data.maxMarks) || 100;
        const obtained = parseInt(data.marksObtained) || 0;

        if (obtained > max) {
          toast.warning(`Marks for ${subjectNames[assessment.courseId]} cannot exceed ${max}`);
          throw new Error('Invalid marks');
        }

        return {
          assessmentId,
          courseId: assessment.courseId,
          maxMarks: max.toString(),
          marksObtained: obtained.toString(),
          gradeObtained: calculateGrade(obtained, max),
        };
      });

    if (performanceList.length === 0) {
      toast.warning('Please enter valid marks for at least one subject');
      return;
    }

    const payload = {
      orgId: activeOrgId,
      eventId,
      userId: selectedStudent,
      academicYear,
      classId,
      remarks,
      assessmentPerformanceList: performanceList,
    };

    setSaving(true);
    try {
      const url = configData.RESOURCES_STUDENT_PERFORMANCE;
      if (existingData?.studentPerformanceId) {
        await axios.put(`${url}/${existingData.studentPerformanceId}`, payload, {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
        });
      } else {
        await axios.post(url, payload, {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
        });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Save failed', err);
      toast.error('Save failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="pfm-overlay" onClick={onClose}>
      <div className="pfm-modal" onClick={e => e.stopPropagation()}>
        <h2>{existingData ? 'Edit' : 'Add'} Performance – {className}</h2>

        <form onSubmit={handleSubmit}>
          {/* Student Selection */}
          <div className="pfm-field">
            <label>Student *</label>
            <input
              type="text"
              placeholder="Search by name or roll number..."
              value={searchStudent}
              onChange={e => setSearchStudent(e.target.value)}
            />
            <select
              value={selectedStudent}
              onChange={e => setSelectedStudent(e.target.value)}
              required
              disabled={!!existingData}
            >
              <option value="">Select Student</option>
              {filteredStudents.map(s => (
                <option key={s.userId} value={s.userId}>
                  {s.name} ({s.rollNumber})
                </option>
              ))}
            </select>
            {loadingStudents && <small>Loading students...</small>}
          </div>

          {/* Subjects Table with Editable Max Marks */}
          <div className="pfm-subjects">
            <label>Enter Marks {loadingSubjects && '(Loading...)'}</label>

            {loadingSubjects ? (
              <p>Loading subjects...</p>
            ) : assessments.length === 0 ? (
              <p>No subjects found for this exam.</p>
            ) : (
              <table className="pfm-subjects-table">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Subject</th>
                    <th style={{ width: '25%' }}>Max Marks</th>
                    <th style={{ width: '25%' }}>Marks Obtained</th>
                    <th style={{ width: '10%' }}>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map(ass => {
                    const subjectName = subjectNames[ass.courseId] || 'Loading...';
                    const data = performanceData[ass.assessmentId] || { marksObtained: '', maxMarks: '100' };
                    const grade = calculateGrade(data.marksObtained, data.maxMarks);

                    return (
                      <tr key={ass.assessmentId}>
                        <td><strong>{subjectName}</strong></td>
                        <td>
                          <input
                            type="number"
                            value={data.maxMarks}
                            onChange={e => handleMarksChange(ass.assessmentId, 'maxMarks', e.target.value)}
                            min="1"
                            max="200"
                            style={{ width: '70px' }}
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={data.marksObtained}
                            onChange={e => handleMarksChange(ass.assessmentId, 'marksObtained', e.target.value)}
                            min="0"
                            max={data.maxMarks || 100}
                            style={{ width: '70px' }}
                            placeholder="0"
                            required
                          />
                        </td>
                        <td>
                          <strong style={{
                            color: grade === 'A+' ? '#0d6efd' :
                              grade === 'A' ? '#198754' :
                                grade === 'B+' || grade === 'B' ? '#0dcaf0' :
                                  grade === 'C' ? '#ffc107' :
                                    grade === 'D' ? '#fd7e14' : '#dc3545',
                            fontSize: '1.1em'
                          }}>
                            {grade}
                          </strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="pfm-field">
            <label>Remarks (Optional)</label>
            <textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              rows={3}
              placeholder="Excellent performance / Needs improvement in..."
            />
          </div>

          <div className="pfm-actions">
            <button type="button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || loadingSubjects || !selectedStudent}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {saving && <CircularProgress size={16} color="inherit" />}
              {saving ? 'Saving...' : existingData ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default PerformanceFormModal;